<script lang="ts">
  import { Copy, Download, Trash2 } from 'lucide-svelte';
  import ConfirmDialog from '$lib/components/ui/ConfirmDialog.svelte';
  import InspectorField from '../InspectorField.svelte';
  import SceneBuilderDialog from './SceneBuilderDialog.svelte';
  import CopyProgrammingDialog from './CopyProgrammingDialog.svelte';
  import LedSceneBuilderDialog from './LedSceneBuilderDialog.svelte';
  import { inputDevicesQuery } from '$lib/queries/input_devices';
  import { ledScenesQuery } from '$lib/queries/led_scenes';
  import { scenesQuery } from '$lib/queries/scenes';
  import { unitSceneLoadsQuery } from '$lib/queries/scene_loads';
  import { roomsQuery } from '$lib/queries/rooms';
  import { unitsQuery } from '$lib/queries/units';
  import { projectsQuery } from '$lib/queries/projects';
  import { updateInputDeviceMutation, deleteInputDeviceMutation } from '$lib/mutations/input_devices';
  import { deleteInputActionMutation } from '$lib/mutations/input_actions';
  import { updateInputKeyMutation } from '$lib/mutations/input_keys';
  import { router, clearEntity } from '$lib/router';
  import { keypadCanvasUI } from '$lib/state/ui/keypadCanvas.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api';
  import {
    generateEngravingSheetPdf,
    engravingSheetCSV,
    downloadTextFile
  } from '$lib/utils/engravingExport';
  import { sceneLoadTargetId } from '$lib/utils/scene_load';
  import { buildLedSceneIndexes, ledSceneForKey, toDomainGraphScenes } from '$lib/utils/led_scene_index';
  import type { InputDeviceResponse } from '$lib/types/input_device';
  import type { InputKeyResponse } from '$lib/types/input_key';

  type DeviceUi = InputDeviceResponse & { name?: string };

  let { id }: { id: number } = $props();

  const unitId = $derived(router.unitId ?? 0);
  const projectId = $derived(router.projectId ?? 0);
  const devices = inputDevicesQuery();
  const ledScenes = ledScenesQuery();
  const unitScenes = scenesQuery();
  const unitSceneLoads = unitSceneLoadsQuery();
  const unitRooms = roomsQuery();
  const units = unitsQuery();
  const projects = projectsQuery();
  const updateDevice = updateInputDeviceMutation();
  const deleteDevice = deleteInputDeviceMutation();
  const deleteKeyAction = deleteInputActionMutation();
  const updateKeyName = updateInputKeyMutation();

  const unit = $derived((units.data ?? []).find((u) => u.id === unitId) ?? null);
  const project = $derived(
    (projects.data ?? []).find((p) => p.id === projectId) ??
      (projects.data ?? []).find((p) => p.id === unit?.project) ??
      null
  );

  const device = $derived((devices.data ?? []).find((d) => d.id === id) as DeviceUi | undefined);
  const allDevices = $derived((devices.data ?? []) as DeviceUi[]);

  const keys = $derived(
    (device?.keys ?? []).sort((a, b) => (a.key_index ?? 0) - (b.key_index ?? 0))
  );

  const ledIndexes = $derived(
    buildLedSceneIndexes(toDomainGraphScenes(ledScenes.data ?? []), allDevices)
  );

  const selectedKey = $derived(
    keys.find((k) => k.id === keypadCanvasUI.selectedKeyId) ?? keys[0] ?? null
  );

  $effect(() => {
    if (keys.length && !keys.some((k) => k.id === keypadCanvasUI.selectedKeyId)) {
      keypadCanvasUI.selectedKeyId = keys[0]?.id ?? null;
    }
  });

  const busy = $derived(
    updateDevice.isPending || deleteDevice.isPending || deleteKeyAction.isPending || updateKeyName.isPending
  );

  let showDeleteConfirm = $state(false);

  async function confirmDeleteDevice() {
    if (!device) return;
    try {
      await deleteDevice.mutateAsync(device.id);
      clearEntity();
      toast.success('Device deleted');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      showDeleteConfirm = false;
    }
  }

  function keyHasLed(key: InputKeyResponse) {
    if (!device) return false;
    const actionIds = new Set((key.actions ?? []).map((a) => a.id));
    return (ledScenes.data ?? []).some((s) => actionIds.has(s.input_action));
  }

  function engravingData(forKeypadOnly = true) {
    const toRow = (d: DeviceUi) => ({
      id: d.id,
      address: d.keys[0]?.address,
      name: d.name,
      location_room: d.location_room
    });
    const kps: DeviceUi[] = forKeypadOnly
      ? device
        ? [device]
        : []
      : ((devices.data ?? []) as DeviceUi[]);
    const keysByKeypad = new Map<number, InputKeyResponse[]>();
    for (const kp of kps) {
      keysByKeypad.set(kp.id, kp.keys ?? []);
    }
    const roomsById = new Map((unitRooms.data ?? []).map((r) => [r.id, r.name]));
    return {
      project: { name: project?.name ?? 'Project' },
      unit: unit ? { name: unit.name } : undefined,
      keypads: kps.map(toRow),
      keysByKeypad,
      roomsById
    };
  }

  async function exportEngravingPdf() {
    if (!device || !unit) return;
    try {
      await generateEngravingSheetPdf(
        engravingData(true),
        `${unit.name}-keypad-${device.id}-engraving.pdf`
      );
      toast.success('Engraving sheet downloaded');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }

  function exportEngravingCsv() {
    if (!device) return;
    const csv = engravingSheetCSV(engravingData(true));
    downloadTextFile(csv, `${unit?.name ?? 'unit'}-keypad-${device.id}-engraving.csv`);
    toast.success('Engraving CSV downloaded');
  }

  function keyName(key: InputKeyResponse) {
    return key?.name?.trim() ? key.name : `Key ${(key.key_index ?? 0) + 1}`;
  }

  function actionFor(key: InputKeyResponse, eventType: string) {
    return (key.actions ?? []).find((a) => a.event_type === eventType);
  }

  function sceneName(sceneId: number) {
    return (unitScenes.data ?? []).find((s) => s.id === sceneId)?.name ?? null;
  }

  const existingSceneForBuilder = $derived.by(() => {
    const act = selectedKey ? actionFor(selectedKey, builderEvent) : null;
    if (!act || act.scene == null) return null;
    const sceneId = act.scene;
    const scene = (unitScenes.data ?? []).find((s) => s.id === sceneId);
    const loads = (unitSceneLoads.data ?? []).filter((sl) => sl.scene === sceneId);
    return {
      id: sceneId,
      name: scene?.name ?? null,
      scope: scene?.scope ?? null,
      scopeId: scene?.scope_id ?? null,
      prefills: loads.map((sl) => ({
        load: sceneLoadTargetId(sl),
        value: sl.value ?? 100
      }))
    };
  });

  async function saveName(key: InputKeyResponse, value: string) {
    const name = value.trim();
    if (name === (key.name ?? '')) return;
    try {
      await updateKeyName.mutateAsync({ id: key.id, data: { name } });
      toast.success('Key name updated');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }

  // Key-level address and group editing. Validation errors (and API errors)
  // are thrown so InspectorField surfaces them inline under the field.
  // Known groups: 1–2 corridor key panels, 3 keypads/logic boards, 4 keycards
  // (see internal/services/input_address.go).
  async function saveAddress(key: InputKeyResponse, value: string) {
    const address = parseInt(value, 10);
    if (isNaN(address) || address < 1 || address > 255) {
      throw new Error('Address must be a whole number between 1 and 255');
    }
    if (address === key.address) return;
    await updateKeyName.mutateAsync({ id: key.id, data: { address } });
  }

  async function saveGroup(key: InputKeyResponse, value: string) {
    const group = parseInt(value, 10);
    if (isNaN(group) || group < 1 || group > 4) {
      throw new Error('Group must be a whole number between 1 and 4');
    }
    if (group === key.group_id) return;
    await updateKeyName.mutateAsync({ id: key.id, data: { group_id: group } });
  }

  function openBuilder(eventType: 'on' | 'off') {
    keypadCanvasUI.showSceneForm = true;
    builderEvent = eventType;
    builderPrefill = eventType === 'off' ? oppositePrefill() : null;
  }

  let builderEvent = $state<'on' | 'off'>('on');
  let builderPrefill = $state<Array<{ load: number; value: number }> | null>(null);
  let showCopyProgramming = $state(false);

  // LED logic entry point on the keypad canvas itself — no precondition that
  // the button already has a scene action (LedSceneBuilderDialog find-or-
  // creates an LED-only InputAction when there isn't one).
  let showLedSceneForm = $state(false);
  let ledBuilderEvent = $state<'on' | 'off'>('on');

  const existingLedSceneForBuilder = $derived(
    selectedKey ? ledSceneForKey(selectedKey, ledBuilderEvent, ledIndexes) : undefined
  );

  function openLedBuilder(eventType: 'on' | 'off') {
    ledBuilderEvent = eventType;
    showLedSceneForm = true;
  }

  const copyCandidates = $derived(
    ((devices.data ?? []) as DeviceUi[]).filter((d) => d.id !== id)
  );

  function oppositePrefill() {
    const onAction = selectedKey ? actionFor(selectedKey, 'on') : null;
    if (!onAction || onAction.action_type !== 'scene') return null;
    return null;
  }

  async function clearAction(key: InputKeyResponse, eventType: 'on' | 'off') {
    const existing = actionFor(key, eventType);
    if (!existing) return;
    try {
      await deleteKeyAction.mutateAsync(existing.id);
      toast.success('Action cleared');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }

  function openCopyProgramming() {
    if (copyCandidates.length === 0) {
      toast.error('No other keypads to copy from');
      return;
    }
    showCopyProgramming = true;
  }
</script>

<div class="p-3 space-y-3">
  <div class="flex items-center justify-between gap-3">
    <div>
      <h2 class="text-base font-medium text-text-primary">
        {device?.name ?? `Keypad ${id}`}
      </h2>
      {#if device?.keys[0]?.address != null}
        <p class="text-xs font-mono text-text-muted">Address {device.keys[0].address}</p>
      {/if}
    </div>
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border-default rounded hover:bg-bg-hover"
        disabled={busy}
        onclick={exportEngravingCsv}
        title="Export engraving CSV"
      >
        <Download class="w-3.5 h-3.5" />
        CSV
      </button>
      <button
        type="button"
        class="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border-default rounded hover:bg-bg-hover"
        disabled={busy}
        onclick={exportEngravingPdf}
        title="Export engraving PDF"
      >
        <Download class="w-3.5 h-3.5" />
        PDF
      </button>
      <button
        type="button"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border-default rounded hover:bg-bg-hover transition-colors duration-100"
        onclick={openCopyProgramming}
      >
        <Copy class="w-3.5 h-3.5" />
        Copy programming
      </button>
      <button
        type="button"
        class="inline-flex items-center gap-1 px-2 py-1 text-xs border border-state-error/40 text-state-error rounded hover:bg-state-error/10 disabled:opacity-50"
        disabled={busy}
        onclick={() => (showDeleteConfirm = true)}
        title="Delete device"
      >
        <Trash2 class="w-3.5 h-3.5" />
        Delete
      </button>
    </div>
  </div>

  {#if keys.length === 0}
    <p class="text-sm text-text-muted">This device has no keys.</p>
  {:else}
    <div class="grid grid-cols-[auto_1fr] gap-6">
      <div class="flex flex-col items-center gap-3">
        <div
          class="rounded-2xl p-3 flex flex-col gap-2 w-[150px] shadow-toyama-md bg-[#1b1b1d] border border-[#3a3a3f]"
          role="listbox"
          aria-label="Keypad buttons"
        >
          {#each keys as key (key.id)}
            {@const isSelected = key.id === keypadCanvasUI.selectedKeyId}
            {@const programmed = actionFor(key, 'on') || actionFor(key, 'off')}
            {@const ledActive = keyHasLed(key)}
            <button
              type="button"
              role="option"
              aria-selected={isSelected}
              class="relative rounded-md min-h-[44px] px-2 py-1.5 flex items-center justify-center text-center transition-shadow duration-100 text-[#f4f4f5] bg-[#2a2a2e]"
              style="border:1px solid {isSelected ? '#97D700' : '#45454c'}; box-shadow:{isSelected
                ? '0 0 0 2px rgba(151,215,0,0.45)'
                : 'none'}"
              onclick={() => (keypadCanvasUI.selectedKeyId = key.id)}
              title={keyName(key)}
            >
              <span class="text-xs font-medium leading-tight line-clamp-2">
                {keyName(key)}
              </span>
              {#if programmed}
                <span
                  class="absolute top-1 right-1 h-1.5 w-1.5 rounded-full"
                  style="background:#97D700"
                  title="Programmed"
                ></span>
              {/if}
              {#if ledActive}
                <span
                  class="absolute bottom-1 right-1 h-1.5 w-1.5 rounded-full ring-1 ring-white/30"
                  style="background:#226A73"
                  title="LED feedback configured"
                ></span>
              {/if}
            </button>
          {/each}
        </div>
        <div class="text-2xs text-text-muted">
          {keys.length}-button
        </div>
      </div>

      {#if selectedKey}
        <div class="rounded-md border border-border-default p-4 space-y-4 self-start">
          <div>
            <div class="text-2xs uppercase tracking-wide text-text-muted mb-1">
              Button {(selectedKey.key_index ?? 0) + 1} — name
            </div>
            <input
              class="w-full rounded border border-border-strong bg-bg-canvas px-2 py-1.5 text-sm text-text-primary"
              value={selectedKey.name ?? ''}
              placeholder={`Key ${(selectedKey.key_index ?? 0) + 1}`}
              disabled={busy}
              onblur={(e) => saveName(selectedKey, e.currentTarget.value)}
              onkeydown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
            />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <InspectorField
              label="Address"
              value={String(selectedKey.address)}
              mono
              onSave={(value) => saveAddress(selectedKey, value)}
            />
            <InspectorField
              label="Group"
              value={String(selectedKey.group_id)}
              mono
              onSave={(value) => saveGroup(selectedKey, value)}
            />
          </div>

          {#each [{ ev: 'on' as const, label: 'On action (press)' }, { ev: 'off' as const, label: 'Off action (release / second press)' }] as row (row.ev)}
            {@const act = actionFor(selectedKey, row.ev)}
            {@const ledScene = ledSceneForKey(selectedKey, row.ev, ledIndexes)}
            <div>
              <div class="text-2xs uppercase tracking-wide text-text-muted mb-1">
                {row.label}
              </div>
              <div class="flex items-center gap-2">
                <div
                  class="flex-1 text-sm {act && act.scene != null ? 'text-text-primary' : 'text-text-muted'} truncate"
                >
                  {#if act && act.scene != null}
                    {sceneName(act.scene) ?? `Scene #${act.scene}`}
                  {:else if act}
                    LED only
                  {:else}
                    Not set
                  {/if}
                </div>
                <button
                  type="button"
                  class="btn-secondary text-xs"
                  disabled={busy}
                  onclick={() => openBuilder(row.ev)}
                >
                  {act ? 'Change…' : 'Set…'}
                </button>
                {#if act}
                  <button
                    type="button"
                    class="text-xs text-state-error hover:underline"
                    disabled={busy}
                    onclick={() => clearAction(selectedKey, row.ev)}
                  >
                    Clear
                  </button>
                {/if}
                <button
                  type="button"
                  class="text-xs text-text-muted hover:text-text-primary hover:underline"
                  onclick={() => openLedBuilder(row.ev)}
                  title="Set LED feedback for this button/event"
                >
                  {ledScene ? 'LED…' : 'LED logic…'}
                </button>
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</div>

<CopyProgrammingDialog
  open={showCopyProgramming}
  targetDevice={device ?? null}
  candidates={copyCandidates}
  onclose={() => (showCopyProgramming = false)}
  onsuccess={() => (showCopyProgramming = false)}
/>

<SceneBuilderDialog
  open={keypadCanvasUI.showSceneForm}
  keypad={device ?? null}
  keyId={selectedKey?.id ?? null}
  eventType={builderEvent}
  existingActionId={selectedKey ? actionFor(selectedKey, builderEvent)?.id ?? null : null}
  existingSceneId={existingSceneForBuilder?.id ?? null}
  existingSceneName={existingSceneForBuilder?.name ?? null}
  existingScope={existingSceneForBuilder?.scope ?? null}
  existingScopeId={existingSceneForBuilder?.scopeId ?? null}
  existingLoads={existingSceneForBuilder?.prefills ?? null}
  prefill={builderPrefill}
  buttonLabel={selectedKey ? keyName(selectedKey) : ''}
  onclose={() => (keypadCanvasUI.showSceneForm = false)}
  onsaved={() => (keypadCanvasUI.showSceneForm = false)}
/>

<LedSceneBuilderDialog
  open={showLedSceneForm}
  device={device ?? null}
  keyId={selectedKey?.id ?? null}
  eventType={ledBuilderEvent}
  existingSceneId={existingLedSceneForBuilder?.id ?? null}
  existingLoads={existingLedSceneForBuilder?.led_loads ?? []}
  buttonLabel={selectedKey ? keyName(selectedKey) : ''}
  onclose={() => (showLedSceneForm = false)}
  onsaved={() => (showLedSceneForm = false)}
/>

<ConfirmDialog
  bind:open={showDeleteConfirm}
  title="Delete device"
  message={`Are you sure you want to delete "${device?.name ?? 'this device'}"? All key programming will be lost.`}
  confirmText="Delete"
  cancelText="Cancel"
  variant="danger"
  onconfirm={confirmDeleteDevice}
  oncancel={() => (showDeleteConfirm = false)}
/>
